import { useMemo, useState } from 'react';
import { Banknote, ShieldAlert, X } from 'lucide-react';

const money = (value) => `$${Number(value || 0).toLocaleString()}`;

export function CareerLoanSharkScene({ open, career, cash, streetRep, busy, onBorrow, onPay, onClose }) {
  const loan = career?.loan || {};
  const [payment, setPayment] = useState('50');
  const balance = Number(loan.balance || 0);
  const active = Boolean(loan.active) && balance > 0;
  const amounts = Array.isArray(loan.availableAmounts) ? loan.availableAmounts : [];
  const maxPayment = Math.max(0, Math.min(Number(cash || 0), balance));
  const parsedPayment = Math.max(0, Math.min(maxPayment, Number.parseInt(payment, 10) || 0));
  const delinquent = Boolean(loan.delinquent);
  const quote = useMemo(() => ({
    vig: Number(loan.currentVigPercent ?? loan.vigPercent ?? 0),
    daily: Number(loan.currentDailyRatePercent ?? (Number(loan.dailyRate || 0) * 100)),
  }), [loan]);

  if (!open) return null;
  return (
    <div className="career-loan-overlay" role="dialog" aria-modal="true" aria-labelledby="career-loan-title">
      <section className="career-loan-scene">
        <div className="career-loan-art" aria-hidden="true"><img src="./scenes/career-loan-shark.webp" alt="" /></div>
        <div className="career-loan-panel glass-panel">
          <header>
            <div><span className="eyebrow">CAREER MODE · BOSS & GUIDO</span><h2 id="career-loan-title">The Loan Shark</h2></div>
            <button onClick={onClose} aria-label="Leave the loan shark"><X /></button>
          </header>
          <p className="career-loan-flavor">“You need it, I lend it. You don't pay it, Guido remembers your address.”</p>
          <div className="career-loan-stats">
            <div><span>Cash</span><strong>{money(cash)}</strong></div>
            <div><span>Rep</span><strong>{streetRep}</strong></div>
            <div><span>Residence credit</span><strong>{money(loan.residenceCreditCap)}</strong></div>
            <div><span>Effective credit</span><strong>{money(loan.effectiveCreditCap)}</strong></div>
          </div>

          {!active ? <>
            <div className="career-loan-terms"><span>Current vig <b>{quote.vig}%</b></span><span>Growth <b>{quote.daily}% per advancing day</b></span></div>
            <p>One active loan at a time. The amount you receive is cash; the vig is added immediately to what you owe.</p>
            <div className="career-loan-actions">
              {amounts.map((amount) => {
                const debt = Math.ceil(amount * (1 + quote.vig / 100));
                return <button key={amount} disabled={busy} onClick={() => onBorrow(amount)}><Banknote /><span><strong>Borrow {money(amount)}</strong><small>Starts at {money(debt)} debt</small></span></button>;
              })}
              {!amounts.length && <p>No credit is available at your current reputation and residence.</p>}
            </div>
          </> : <>
            <div className={`career-loan-balance ${delinquent ? 'delinquent' : ''}`}>
              <span>{delinquent ? <ShieldAlert /> : <Banknote />} Current balance</span><strong>{money(balance)}</strong>
              <small>Principal {money(loan.principal)} · opened Day {loan.openedDay || '?'} · {Number(loan.dailyRate || 0) * 100}% daily growth</small>
              {delinquent && <em>DELINQUENT · {loan.daysSinceMeaningfulPayment} days since a meaningful payment · collector pressure {Math.round(Number(loan.collectorChance || 0) * 100)}%</em>}
            </div>
            <div className="career-loan-payment">
              <label>Custom payment<input type="number" min="1" max={maxPayment} value={payment} onChange={(event) => setPayment(event.target.value)} /></label>
              <button disabled={busy || parsedPayment <= 0} onClick={() => onPay(parsedPayment)}>Pay {money(parsedPayment)}</button>
              <button disabled={busy || maxPayment <= 0} onClick={() => onPay(Math.min(maxPayment, Math.ceil(balance / 2)))}>Pay Half</button>
              <button disabled={busy || maxPayment < balance} onClick={() => onPay(balance)}>Pay It Off</button>
            </div>
            <p className="career-loan-note">A meaningful payment resets collector pressure. You cannot open another loan until this balance reaches zero.</p>
          </>}
          <button className="career-loan-leave" onClick={onClose} disabled={busy}>Leave before Guido gets conversational</button>
        </div>
      </section>
    </div>
  );
}
